// Simple line-level LCS diff renderer with red/green blocks.
// Good enough for typical proposal-sized docs (<5k lines).

function lcsMatrix(a, b) {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, () => new Uint32Array(n + 1));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  return dp;
}

function diffLines(a, b) {
  const dp = lcsMatrix(a, b);
  const out = [];
  let i = a.length, j = b.length;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) { out.push({ t: "same", line: a[i - 1] }); i--; j--; }
    else if (dp[i - 1][j] >= dp[i][j - 1]) { out.push({ t: "del", line: a[i - 1] }); i--; }
    else { out.push({ t: "add", line: b[j - 1] }); j--; }
  }
  while (i > 0) { out.push({ t: "del", line: a[--i] }); }
  while (j > 0) { out.push({ t: "add", line: b[--j] }); }
  return out.reverse();
}

export default function DiffView({ original = "", revised = "", className = "" }) {
  const a = original.split(/\r?\n/);
  const b = revised.split(/\r?\n/);
  const rows = diffLines(a, b);
  const stats = rows.reduce((acc, r) => { acc[r.t]++; return acc; }, { same: 0, add: 0, del: 0 });

  return (
    <div className={`border bg-card font-mono text-xs leading-relaxed ${className}`} data-testid="diff-view">
      <div className="px-4 py-2 border-b bg-muted/50 flex items-center gap-4 text-[10px] uppercase tracking-wider">
        <span className="text-muted-foreground">Diff</span>
        <span className="text-emerald-700 dark:text-emerald-400">+ {stats.add} added</span>
        <span className="text-red-700 dark:text-red-400">− {stats.del} removed</span>
        <span className="text-muted-foreground ml-auto">{stats.same} unchanged</span>
      </div>
      <div className="overflow-auto max-h-[60vh]">
        {rows.map((r, i) => {
          const bg = r.t === "add" ? "bg-emerald-50 dark:bg-emerald-950/30 border-l-2 border-emerald-500"
                   : r.t === "del" ? "bg-red-50 dark:bg-red-950/30 border-l-2 border-red-500"
                   : "border-l-2 border-transparent";
          const marker = r.t === "add" ? "+" : r.t === "del" ? "−" : " ";
          const textCls = r.t === "add" ? "text-emerald-900 dark:text-emerald-100"
                        : r.t === "del" ? "text-red-900 dark:text-red-100 line-through opacity-80"
                        : "text-foreground/70";
          return (
            <div key={i} className={`grid grid-cols-[32px_1fr] items-start ${bg}`} data-testid={`diff-row-${r.t}-${i}`}>
              <div className="text-center text-muted-foreground select-none pt-0.5">{marker}</div>
              <div className={`whitespace-pre-wrap break-words pr-3 py-0.5 ${textCls}`}>{r.line || "\u00A0"}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
